import { useEffect, useRef, useState } from 'react';
import { useSettingsStore } from '@/features/settings/stores/settings.store';
import { toast } from 'react-hot-toast';
import { AlertCircle, Mail, Plus, Trash2 } from 'lucide-react';
import { logger } from '@/features/help/utils/logger';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Purpose: Normalize the recipient input list into a deduplicated, backend-ready array.
const normalizeNotificationEmails = (values: string[]): string[] => {
  const deduped = new Set<string>();

  values
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .forEach((email) => {
      deduped.add(email);
    });

  return Array.from(deduped);
};

// Purpose: Compare notification email arrays after normalization so UI dirty-state stays stable.
const areEmailArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

export const NotificationsSection = () => {
  const {
    notificationEmails,
    notifyCrmOwner,
    brokerNotificationDelaySeconds,
    applicantReminderEnabled,
    applicantReminderCadenceHours,
    applicantReminderMaxReminders,
    isLoading,
    isSaving,
    error,
    updateSettings,
  } = useSettingsStore();

  const [recipientInputs, setRecipientInputs] = useState<string[]>([]);
  const [notifyOwner, setNotifyOwner] = useState(false);
  const [delayMinutesInput, setDelayMinutesInput] = useState('3');
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [reminderCadenceHoursInput, setReminderCadenceHoursInput] = useState('24');
  const [reminderMaxRemindersInput, setReminderMaxRemindersInput] = useState('3');
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);
  const recipientInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setRecipientInputs(notificationEmails);
    setNotifyOwner(notifyCrmOwner);
    setDelayMinutesInput(String(Math.max(1, Math.round(brokerNotificationDelaySeconds / 60))));
    setRemindersEnabled(applicantReminderEnabled);
    setReminderCadenceHoursInput(String(applicantReminderCadenceHours));
    setReminderMaxRemindersInput(String(applicantReminderMaxReminders));
  }, [
    notificationEmails,
    notifyCrmOwner,
    brokerNotificationDelaySeconds,
    applicantReminderEnabled,
    applicantReminderCadenceHours,
    applicantReminderMaxReminders,
  ]);

  useEffect(() => {
    if (pendingFocusIndex === null) {
      return;
    }

    recipientInputRefs.current[pendingFocusIndex]?.focus();
    setPendingFocusIndex(null);
  }, [pendingFocusIndex, recipientInputs]);

  const handleRecipientChange = (index: number, value: string) => {
    setRecipientInputs((current) =>
      current.map((recipient, currentIndex) => (currentIndex === index ? value : recipient))
    );
  };

  const handleAddRecipient = () => {
    setPendingFocusIndex(recipientInputs.length);
    setRecipientInputs((current) => [...current, '']);
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipientInputs((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleSave = async () => {
    const trimmedRecipients = recipientInputs.map((recipient) => recipient.trim().toLowerCase());
    const hasEmptyRecipientRow = recipientInputs.some((recipient) => recipient.trim().length === 0);
    if (hasEmptyRecipientRow) {
      toast.error('Finish or remove empty notification recipients before saving');
      return;
    }

    const invalidEmail = trimmedRecipients.find((email) => !EMAIL_REGEX.test(email));
    if (invalidEmail) {
      toast.error(`Invalid notification email: ${invalidEmail}`);
      return;
    }

    const normalizedEmails = normalizeNotificationEmails(trimmedRecipients);

    const delayMinutes = Number.parseInt(delayMinutesInput, 10);
    if (!Number.isFinite(delayMinutes) || delayMinutes < 1 || delayMinutes > 10) {
      toast.error('Internal notification delay must be between 1 and 10 minutes');
      return;
    }

    const cadenceHours = Number.parseInt(reminderCadenceHoursInput, 10);
    if (!Number.isFinite(cadenceHours) || cadenceHours < 1 || cadenceHours > 168) {
      toast.error('Reminder cadence must be between 1 and 168 hours');
      return;
    }

    const maxReminders = Number.parseInt(reminderMaxRemindersInput, 10);
    if (!Number.isFinite(maxReminders) || maxReminders < 1 || maxReminders > 10) {
      toast.error('Maximum reminders must be between 1 and 10');
      return;
    }

    logger.info('SettingsFlow', 'Saving notification settings', {
      notificationEmailCount: normalizedEmails.length,
      notifyOwner,
      delayMinutes,
      remindersEnabled,
      cadenceHours,
      maxReminders,
    });

    const success = await updateSettings({
      notificationEmails: normalizedEmails,
      notifyCrmOwner: notifyOwner,
      brokerNotificationDelaySeconds: delayMinutes * 60,
      applicantReminderEnabled: remindersEnabled,
      applicantReminderCadenceHours: cadenceHours,
      applicantReminderMaxReminders: maxReminders,
    });

    if (success) {
      logger.success('SettingsFlow', 'Notification settings saved successfully');
    }
  };

  const normalizedEmails = normalizeNotificationEmails(recipientInputs);
  const normalizedDelaySeconds = Number.parseInt(delayMinutesInput, 10) * 60;
  const normalizedCadenceHours = Number.parseInt(reminderCadenceHoursInput, 10);
  const normalizedMaxReminders = Number.parseInt(reminderMaxRemindersInput, 10);

  const isSaveDisabled =
    isSaving ||
    isLoading ||
    (areEmailArraysEqual(normalizedEmails, notificationEmails) &&
      notifyOwner === notifyCrmOwner &&
      normalizedDelaySeconds === brokerNotificationDelaySeconds &&
      remindersEnabled === applicantReminderEnabled &&
      normalizedCadenceHours === applicantReminderCadenceHours &&
      normalizedMaxReminders === applicantReminderMaxReminders);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Settings</h2>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-lg">
          <div className="flex gap-2 items-center">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-8">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Internal Notifications
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Configure who should receive broker-side application updates.
            </p>
          </div>

          <div>
            <div className="block text-sm font-medium text-gray-700 mb-2">
              Notification recipients
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                  {normalizedEmails.length} recipient{normalizedEmails.length === 1 ? '' : 's'}
                </div>
              </div>

              {recipientInputs.length === 0 ? (
                <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                  No recipients added yet.
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  {recipientInputs.map((recipient, index) => (
                    <div
                      key={`notification-recipient-${index}`}
                      className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center"
                    >
                      <div className="flex flex-1 items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                          <Mail className="h-4 w-4" />
                        </div>
                        <input
                          ref={(element) => {
                            recipientInputRefs.current[index] = element;
                          }}
                          type="email"
                          value={recipient}
                          onChange={(event) => handleRecipientChange(index, event.target.value)}
                          disabled={isLoading}
                          aria-label={`Notification recipient ${index + 1}`}
                          placeholder={`recipient${index + 1}@company.com`}
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(index)}
                        disabled={isLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={handleAddRecipient}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  <Plus className="h-4 w-4" />
                  Add recipient
                </button>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="broker-delay"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Internal notification delay (minutes)
            </label>
            <input
              id="broker-delay"
              type="number"
              min={1}
              max={10}
              step={1}
              value={delayMinutesInput}
              onChange={(event) => setDelayMinutesInput(event.target.value)}
              disabled={isLoading}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              Broker progress and completion notifications will wait this long before sending.
            </p>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="notify-crm-owner"
                type="checkbox"
                checked={notifyOwner}
                onChange={(event) => setNotifyOwner(event.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="notify-crm-owner" className="font-medium text-gray-700">
                Notify the assigned CRM record owner
              </label>
              <p className="text-gray-500">
                When enabled, the user assigned to the record in the CRM will also receive
                internal notifications.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 space-y-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Applicant Reminders
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Start reminders after the applicant opens the application for the first time but
              does not complete it.
            </p>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="applicant-reminders-enabled"
                type="checkbox"
                checked={remindersEnabled}
                onChange={(event) => setRemindersEnabled(event.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor="applicant-reminders-enabled"
                className="font-medium text-gray-700"
              >
                Enable applicant reminders
              </label>
              <p className="text-gray-500">
                Reminders are only scheduled after the first application open is recorded.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="applicant-reminder-cadence"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reminder cadence (hours)
              </label>
              <input
                id="applicant-reminder-cadence"
                type="number"
                min={1}
                max={168}
                step={1}
                value={reminderCadenceHoursInput}
                onChange={(event) => setReminderCadenceHoursInput(event.target.value)}
                disabled={isLoading || !remindersEnabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label
                htmlFor="applicant-reminder-max"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Maximum reminders
              </label>
              <input
                id="applicant-reminder-max"
                type="number"
                min={1}
                max={10}
                step={1}
                value={reminderMaxRemindersInput}
                onChange={(event) => setReminderMaxRemindersInput(event.target.value)}
                disabled={isLoading || !remindersEnabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div className="flex">
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className={`px-4 py-2 rounded ${
              isSaveDisabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } transition-colors`}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};
